import { marked, Renderer } from "marked";

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

class HelpRenderer extends Renderer {
  override heading({ tokens, depth }: { tokens: any[]; depth: number }): string {
    const text = this.parser.parseInline(tokens);
    const id = slugify(text);
    return `<h${depth} id="${id}">${text}</h${depth}>`;
  }

  override link({ href, tokens }: { href: string; tokens: any[] }): string {
    const text = this.parser.parseInline(tokens);
    if (!href) return text;
    if (href.startsWith("http://") || href.startsWith("https://")) {
      return `<a href="${href}" rel="noopener noreferrer nofollow" target="_blank">${text}</a>`;
    }
    if (href.startsWith("mailto:")) {
      return `<a href="${href}">${text}</a>`;
    }
    const cleanHref = href.startsWith("/") ? href : "/" + href;
    return `<a href="${cleanHref}">${text}</a>`;
  }

  override image({ href, title, text }: { href: string; title: string | null; text: string }): string {
    if (!href) return text || "";
    if (href.startsWith("/help-assets/")) {
      const alt = text || "";
      const titleAttr = title ? ` title="${title}"` : "";
      return `<img src="${href}" alt="${alt}"${titleAttr} loading="lazy" />`;
    }
    return super.image({ href, title, text } as any);
  }
}

marked.setOptions({
  renderer: new HelpRenderer(),
  gfm: true,
  breaks: false,
});

export interface ParsedMarkdown {
  html: string;
  headings: { level: number; text: string; id: string }[];
}

export function renderMarkdown(markdown: string): ParsedMarkdown {
  const youtubeRegex = /\{\{youtube:([a-zA-Z0-9_-]{11})\}\}/g;
  const headings: { level: number; text: string; id: string }[] = [];

  const preprocessed = markdown.replace(youtubeRegex, (_match, videoId: string) => {
    return `\n\n<iframe-youtube video-id="${videoId}"></iframe-youtube>\n\n`;
  });

  const tokens = marked.lexer(preprocessed);

  for (const token of tokens) {
    if (token.type === "heading") {
      const id = slugify(token.text);
      headings.push({ level: token.depth, text: token.text, id });
    }
  }

  const html = marked.parser(tokens);

  const finalHtml = html.replace(
    /<iframe-youtube video-id="([a-zA-Z0-9_-]{11})"><\/iframe-youtube>/g,
    (_match, videoId: string) => {
      return `<div class="youtube-embed"><iframe src="https://www.youtube-nocookie.com/embed/${videoId}" title="YouTube video player" loading="lazy" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe></div>`;
    }
  );

  return { html: finalHtml, headings };
}
